'use client'

import { useEffect, useState } from "react"

import classNames from "classnames"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { EditableContent, Tag } from 'shared/ui'
import { TagsSelector } from "shared/ui/TagsSelector"
import styled from "styled-components"

import { currentBoardIdSelector } from "../model/selectors/boardsSelectors"
import { renameBoardThunk } from "../model/slice/boardsSlice"


interface BoardHeaderProps {
  title: string
  boardTags: string[]
  systemTags: Array<{ id: string; value: string }>
  description: string
  onChangeTags: (tag: any) => void
  onCreateTag: (value: string) => void
  onChangeDescription: (value: string) => void
  disabled?: boolean
  withoutUserInteraction?: boolean
}

export const TAG_COLORS = [
  'magenta',
  'red',
  'volcano',
  'orange',
  'gold',
  'lime',
  'green',
  'cyan',
  'blue',
  'geekblue',
  'purple'
]

export const STATUS_COLORS = {
  'In Progress': TAG_COLORS[0],
  'Needs Review': TAG_COLORS[1],
  'Needs Credit': TAG_COLORS[2],
  'Hold': TAG_COLORS[3],
}

export const BoardHeader = ({ title, boardTags, systemTags, description, onChangeTags, onCreateTag, onChangeDescription, disabled, withoutUserInteraction }: BoardHeaderProps) => {

  const [descriptionValue, setDescriptionValue] = useState(description || '')
  const [titleValue, setTitleValue] = useState(title)
  const [titleError, setTitleError] = useState(false)
  const dispatch = useAppDispatch()
  const boardId = useAppSelector(currentBoardIdSelector)


  const systemTagsList = systemTags.map((sysTag) => {
    return {
      label: sysTag.value,
      value: sysTag.id,
      id: sysTag.id,
    }
  }).filter((systemTag) => {
    return !boardTags.includes(systemTag.id)
  })

  const boardTagsList = boardTags.map((tagId) => ({
    label: systemTags.find((t) => t.id === tagId)?.value,
    id: tagId,
    value: tagId,
  }))

  useEffect(() => {
    setDescriptionValue(description || '')
  }, [description])

  useEffect(() => {
    setTitleValue(title)
  }, [title])

  const handleDescriptionBlur = (value: string) => {
    const nextValue = value || ''
    setDescriptionValue(nextValue)

    if (nextValue !== (description || '')) {
      onChangeDescription(nextValue)
    }
  }

  const handleTitleFinish = (value: string) => {
    if (!value.trim()) {
      setTitleError(true)
      return
    } else if (value.length > 255) {
      setTitleValue(title)
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'File name is too long',
        message: 'Please try again',
      })
    } else {
      setTitleError(false)
      setTitleValue(value)
      dispatch(renameBoardThunk({ name: value, boardId }))
    }
  }

  return (
    <SBoardHeader className={classNames({
      'without-user-interaction': withoutUserInteraction,
    })}
    >
      <STitleEditableContent
        value={titleValue}
        onChange={setTitleValue}
        onBlur={handleTitleFinish}
        placeholder="Enter board title"
        className={titleError ? 'error' : ''}
        disabled={disabled}
        maxLines={3}
      />

      <SStyledEditableContent
        value={descriptionValue}
        onChange={setDescriptionValue}
        onBlur={handleDescriptionBlur}
        placeholderColor="rgba(0, 0, 0, 0.45)"
        placeholder="Click to add description"
        disabled={disabled}
      />

      <STagsSelector
        className="board-tags-selector"
        classNameTagsContainer="board-tags-selector-container"
        options={systemTagsList}
        value={boardTagsList}
        onSelect={onChangeTags}
        onChange={onChangeTags}
        onCreateTag={onCreateTag}
        disabled={disabled}
        showBorder={false}
        mode='select-create'
      />
    </SBoardHeader>
  )
}

const STagsSelector = styled(TagsSelector)`
  margin-top: 32px;
  border: 1px solid transparent;

  &.board-tags-selector-container{
    margin-left: -12px;
    margin-top: 26px;
    padding: 9px 16px;
    border-radius: 4px;
  }

  &.board-tags-selector{
    .ant-select-selector{

      &:hover {
        /* background-color: #F5F5F5 !important; */
        border-radius: 6px !important;
        cursor: pointer !important;
      }
    } 
}
`

const SBoardHeader = styled.div`
  max-width: 600px;

  .ant-btn {
    margin-top: 32px;
  }

  &.without-user-interaction {
    pointer-events: none;
    cursor: default;
  }
`

const SBoardTags = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
`

const SBoardTag = styled(Tag)`
  margin-right: 4px;
`

const SStyledEditableContent = styled(EditableContent)`
  margin-top: 20px;
  margin-left: -10px;
  max-width: 900px;
  
  &:not([contenteditable="true"]) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }
`

const STitleEditableContent = styled(EditableContent)`
  margin-top: -4px;
  margin-bottom: 4px;
  padding: 0 8px;
  max-width: 100%;
  margin-left: -8px;
  
  &:not([contenteditable="true"]) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }
  
  &.error {
    border-color: #ff4d4f;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.1);
  }
  
  font-size: 32px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.88);
  
  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.10);
  }
`
